import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FieldWorkRequest } from '../../domain/fieldWorkRequest';
import { NewFulfillmentOrderDrawer } from '../fulfillment/NewFulfillmentOrderDrawer';

/**
 * Asked right after a field work request is created: most requests need
 * parts, so offer to start its pick list there and then. "Create pick
 * list" opens the new order drawer for the request's customer and site.
 */
export function PickListPrompt({
  request,
  onDone,
}: {
  request: FieldWorkRequest;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const dialogId = useId();

  if (creating) {
    return (
      <NewFulfillmentOrderDrawer
        defaults={{
          customerId: request.customerId,
          siteId: request.siteId,
          fieldWorkRequestId: request.id,
          fieldWorkRequestTitle: request.title || 'Untitled request',
        }}
        onClose={onDone}
        onCreated={(order) => void navigate(`/fulfillment/${order.id}`)}
      />
    );
  }

  return (
    <div className="drawer-overlay centered-overlay" role="presentation" onClick={onDone}>
      <div
        className="confirmation-dialog"
        role="alertdialog"
        aria-labelledby={`${dialogId}-title`}
        aria-describedby={`${dialogId}-body`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="drawer-heading">
          <h2 id={`${dialogId}-title`}>Does this request need a pick list?</h2>
        </div>
        <p id={`${dialogId}-body`}>
          Create the pick list now so the warehouse knows exactly what to pack for “
          {request.title || 'Untitled request'}”. You can also add one later from the request.
        </p>
        <div className="dialog-actions">
          <button type="button" onClick={onDone}>
            Not now
          </button>
          <button type="button" className="primary" autoFocus onClick={() => setCreating(true)}>
            Create pick list
          </button>
        </div>
      </div>
    </div>
  );
}
